import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cssMap } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import { useSuggestionsLoadingMessages } from '../useRotatingLoadingMessage.ts';

const styles = cssMap({
	container: {
		// Sticks above the footer once the list overflows; the panel's `gap` spaces it from the last card.
		position: 'sticky',
		// Offset is inside the layout's 80px bottom padding; -6px puts the bar 14px above Discard all.
		insetBlockEnd: token('space.negative.075'),
		// Above the footer (`zIndex: 1`); otherwise the Discard all container's fade covers the bar's bottom shadow.
		zIndex: 2,
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		paddingInline: token('space.200'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.rovo.surface.overlay'),
		// Fades the shadow in/out as the bar sticks and unsticks.
		boxShadow: '0 0 0 0 transparent',
		transitionProperty: 'box-shadow',
		transitionDuration: token('motion.duration.medium'),
		transitionTimingFunction: token('motion.easing.inout.bold'),
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0s',
		},
	},
	// Only while stuck over the list; at rest the bar sits flush after the last card.
	cardStuck: {
		boxShadow: token('elevation.shadow.overflow'),
	},
	// Zero-height marker at the bar's natural (unstuck) bottom edge. The negative margin
	// cancels the panel's `gap` (`space.200`) so it does not shift the layout.
	stuckSentinel: {
		height: '0px',
		marginBlockStart: token('space.negative.200'),
		pointerEvents: 'none',
	},

	barRow: {
		width: '100%',
		paddingBlock: token('space.150'),
	},

	placeholderAvatarSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '24px',
		height: '24px',
	},
});

/** The agent doing the review, as exposed by the AI suggestions plugin state. */
export type SuggestionsInlineLoadingBarAgent = {
	id: string;
	identityAccountId?: string | null;
	name: string;
	namedId?: string;
};

type SuggestionsInlineLoadingBarProps = {
	/**
	 * The agent doing the review. It is named in the first loading message, and its
	 * avatar is shown alongside that message (then the bar switches to the usual
	 * loading shape), matching the editor's thinking bar. Falls back to the generic
	 * Rovo copy and loading shape when absent.
	 */
	agent?: SuggestionsInlineLoadingBarAgent;
	api?: FullPageEditorPresetAPI;
	/**
	 * `true` while the reviewing agent is still loading. The bar then holds on "Preparing
	 * agent" with a placeholder agent avatar (no rotation) until `agent` resolves.
	 */
	isAgentLoading?: boolean;
	/**
	 * Epoch ms the message rotation is anchored to (from shared plugin state): when the
	 * reviewing agent became ready, falling back to when the thinking session started.
	 * Passed through so this bar stays in sync with the editor's thinking bar.
	 */
	startedAt?: number | null;
	/** Message rotation starts when the agent is ready; defaults to the session start. */
	rotationStartedAt?: number | null;
	/** Shared facade target so this bar fills to the same value as the editor's thinking bar. */
	progressTarget?: number | null;
};

/**
 * Compact loading bar shown at the bottom of the suggestions list while Rovo
 * regenerates with existing suggestions still visible. Reuses the editor's
 * `RovoThinkingBar` so it stays in sync with the full-page loading bar.
 * Sticks above the footer when the list scrolls, so it is always visible; the shadow
 * only shows while it is stuck over content.
 */
export const SuggestionsInlineLoadingBar = ({
	agent,
	api,
	isAgentLoading = false,
	startedAt,
	rotationStartedAt,
	progressTarget,
}: SuggestionsInlineLoadingBarProps): React.JSX.Element => {
	// No scroll-into-view: the sticky bar is always visible.
	const rotatingMessages = useSuggestionsLoadingMessages({
		agentName: agent?.name,
		isAgentLoading,
	});
	const sentinelRef = useRef<HTMLDivElement>(null);
	const [isStuck, setIsStuck] = useState(false);

	// The bar sticks once its natural position falls below the scroll area's content box
	// (the layout's 80px bottom padding is excluded) plus the container's 6px inset. The
	// sentinel sits at that natural position, so the root margin (-80px + 6px) makes it stop
	// intersecting the moment the bar is no longer resting at the end of the list.
	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || typeof IntersectionObserver === 'undefined') {
			return;
		}
		const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
			rootMargin: '0px 0px -74px 0px',
		});
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, []);

	const fireCancelledAnalytics = useCancelledAnalytics(api?.analytics?.actions.fireAnalyticsEvent);

	const handleStop = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	const agentId = agent?.id;
	const agentNamedId = agent?.namedId;
	const agentIdentityAccountId = agent?.identityAccountId;
	const firstMessage = useMemo(() => {
		if (agentId) {
			return {
				icon: (
					<AgentAvatar
						agentId={agentId}
						agentNamedId={agentNamedId}
						agentIdentityAccountId={agentIdentityAccountId}
						size="small"
						showBorder={false}
					/>
				),
			};
		}
		if (isAgentLoading) {
			return {
				icon: (
					<span css={styles.placeholderAvatarSlot}>
						<Avatar
							appearance="hexagon"
							size="small"
							borderColor="transparent"
							testId="suggestions-panel-preparing-agent-avatar"
						/>
					</span>
				),
			};
		}
		return undefined;
	}, [agentId, agentNamedId, agentIdentityAccountId, isAgentLoading]);

	return (
		<>
			<div css={styles.container} data-testid="suggestions-panel-inline-loading-bar">
				<div
					css={[styles.card, isStuck && styles.cardStuck]}
					data-testid="suggestions-panel-inline-loading-bar-card"
				>
					<div css={styles.barRow}>
						<RovoThinkingBar
							onStop={handleStop}
							showProgressBar={isExperimentEnabled(
								'platform_editor_ai_content_suggested_edits_labs',
							)}
							useRotatingTitle
							customRotatingMessages={rotatingMessages}
							startedAt={startedAt}
							rotationStartedAt={
								isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')
									? rotationStartedAt
									: undefined
							}
							progressTarget={
								isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')
									? progressTarget
									: undefined
							}
							firstMessage={firstMessage}
						/>
					</div>
				</div>
			</div>
			<div ref={sentinelRef} css={styles.stuckSentinel} aria-hidden="true" />
		</>
	);
};
